import {Component, Event, type EventEmitter, h, Host, Listen, Prop, State, Watch, Element} from '@stencil/core';
import type {CoseRequestService} from '../../../../../utils/services/cose/cose-request.service';
import type {FacetsV2Service} from '../../../../../utils/services/facets/facets-v2.service';
import type {FacetsData} from '../../facets-build-wrapper';
import {cloneObjectWithNoReference} from '../../../../../utils/helpers/clone-object-with-no-reference';
import type {FacetChildUiModel, FacetTreeUiModel} from '../../../../../models/ui/facets/facet-tree-ui.model';
import type {NewFacetsLogicConfig} from '../../../../strategies/types/facets-config-strategy-data.type';
import {DEFAULT_FACET_LOGIC_CONFIG_CONSTANT} from '../../constants/default-facet-logic-config.constant';
import type {FilterNode} from '../../filter-node';
import {FilterChangeContext} from '../../models/filter-change-context.enum';
import {makeLogger} from '../../../../../logger/logger';
import {FILTERS_ID_SEPARATOR} from '../../../../../constants/filterConstants';
import type { labelProviderInterface } from '../../../../LabelProviderInterface';

const { info, warn, debug } = makeLogger({
  pathToCurrentFile: 'components/result-page/facets-build-wrapper/components/default-filters-wrapper/default-filters-wrapper.tsx',
  loggersList: ['info', 'warn', 'debug']
});

@Component({
  tag: 'default-filters-wrapper',
  styleUrl: './default-filters-wrapper.scss',
  shadow: true,
})
export class DefaultFiltersWrapper {
  @Element() el;
  @Prop({ mutable: true}) labelProvider?: labelProviderInterface;
  @Prop() readonly hiddenFacets: string[] = [];
  @Prop() readonly coseRequestService?: CoseRequestService;
  @Prop() readonly facetsService?: FacetsV2Service;
  @Prop() readonly tabName: string;
  @Prop() readonly facetsDataProp!: string;
  @Prop() readonly dividerFacets?: string[];
  @Prop() readonly facetsLabel?: string;
  @Prop() readonly facetsLogicConfig: NewFacetsLogicConfig = DEFAULT_FACET_LOGIC_CONFIG_CONSTANT;
  @Prop() readonly filterPremiumIconUniqueId?: string;
  @Prop() readonly mobileView: boolean = false;
  @Prop() readonly rtl: boolean;

  @State() productCatalogFacetToggleVisible = false;
  @Watch('facetsDataProp')
  onFacestDataChange() {
      if(this.facetsDataState) {
        this.refactorFacetsList();
      }
  }

  @Event({eventName: 'filter:did:change', bubbles: true}) filterDidChange: EventEmitter<{filterNode: FilterNode, context: FilterChangeContext, facetCategoryDisplayName?: string}>;
  @Event({eventName: 'filters:did:change', bubbles: true}) filtersDidChange: EventEmitter<{filterNodes: FilterNode[], context: FilterChangeContext}>;

  private facetsDataState: FacetsData;
  private filteredLocalFacets: FacetTreeUiModel[];

  get isDividerFacetsExists(): boolean {
      return this.dividerFacets && this.dividerFacets?.length > 0;
  }

  componentWillLoad(): void {
      if (!this.labelProvider) {
        info('Assigning default labelProvider to reset button component');
        this.labelProvider = (name, fallback) => {
            warn(
              'LabelProvider not provided in component',
              [name, fallback]
            );
            return fallback;
        };
      }
      this.refactorFacetsList();
  }

  private refactorFacetsList() {
      const facetsData = JSON.parse(this.facetsDataProp);
      const firstFacets = [];
      let refactorFacets = [];
      const filtersToRefactor = cloneObjectWithNoReference((this.isDividerFacetsExists) ? facetsData.filteredFacets : facetsData.facets);

      filtersToRefactor.map(facet => {
          if(facet.id === 'catalog') {
            firstFacets.push(facet);
          }
      });

      refactorFacets = filtersToRefactor.filter((facet) => facet.id !== 'catalog');

      firstFacets.forEach(facet => {
          refactorFacets.unshift(facet);
      });

      if(this.isDividerFacetsExists) {
        facetsData.filteredFacets = refactorFacets;
      } else {
        facetsData.facets = refactorFacets;
      }

      this.filteredLocalFacets = refactorFacets;
      this.facetsDataState = facetsData;
  }

  private tooltip() {
      return <span class="tooltip">
        <se-tooltip
          color="alternative"
          position="right"
        >
          <qds-button
            slot="trigger"
            importance="subdued"
            size="small"
            icon-name="information-circle"
            icon-only="true"
          ></qds-button>
          <se-tooltip-content class={'svs-status__content'}>
            {this.labelProvider(
              'filters.tooltip-content',
              'Apply characteristics filters to narrow\n down your search results.',
            )}
          </se-tooltip-content>
        </se-tooltip>
      </span>;
  }

  render() {
      return (
        <Host>
          <facets-block>
            {this.facetsDataState && this.filteredLocalFacets ? <facets-block-controls
              facetsLabel={this.facetsLabel}
              resetLabel={this.labelProvider(`${this.tabName}.facet.facets.reset`, 'Reset All')}
              showReset={(this.facetsDataState.facetsState.length > 0) ? 'true' : 'false'}
              slot="facets-block-top">
            </facets-block-controls> : ''}
            <div slot="facets-list">
              {(this.facetsLogicConfig.showChildren === 'allways' || this.facetsLogicConfig.showChildren === 'whenParentSelected') ?
                this.renderFilteredFacets()
                : ''}
              {(this.facetsLogicConfig.showChildren === 'allways' || this.facetsLogicConfig.showChildren === 'whenParentSelected') ?
                this.renderOuterFacets()
                : ''}
              {(this.facetsDataState.onlyLastChilds && this.facetsLogicConfig.showChildren === 'onlyLastChilds') ?
                this.renderOnlyLastChildFacets()
                : ''}
            </div>
          </facets-block>
        </Host>
      );
  }

  private renderFilteredFacets() {
      return (this.facetsDataState && this.filteredLocalFacets)
        ? this.renderNode(cloneObjectWithNoReference<FacetTreeUiModel[]>(this.filteredLocalFacets as FacetTreeUiModel[]), true, 0) : '';
  }

  private renderOuterFacets() {
      return (this.facetsDataState && this.facetsDataState?.outerFacets && this.facetsDataState.outerFacets?.length > 0)
        ? this.renderNode(cloneObjectWithNoReference<FacetTreeUiModel[]>(this.facetsDataState.outerFacets as unknown as FacetTreeUiModel[]), true, 0) : '';
  }

  private renderOnlyLastChildFacets() {
      return this.renderNode(cloneObjectWithNoReference<FacetTreeUiModel[]>(this.facetsDataState.onlyLastChilds as FacetTreeUiModel[]), true, 0);
  }


  private renderNode(children: FacetTreeUiModel[] | FacetChildUiModel[], isRoot = true, level) {
      level++;
      let characteristicsDisplayed = false;

      return (
        <div>
          {children.map((f, index:number) => {
              if(f.expression === 'SINGLE') {
                if(!this.hiddenFacets.includes(f['id']) && (f.children?.length > 0)) {
                  return (
                    <single-facet-node
                      preselectedFilters={this.facetsLogicConfig.preselectedFilters || []}
                      filterNode={f as FilterNode}
                      labelProvider={this.labelProvider}
                      tabName={this.tabName}
                      onChanged={(e: CustomEvent) => {
                          this.filtersDidChange.emit({filterNodes: e.detail.children, context: FilterChangeContext.desktop});
                      }}
                      key={index + f['count']}
                    >
                    </single-facet-node>
                  );
                }
              }
              if (!this.hiddenFacets.includes(f['id']) && (f.children?.length > 0)) {
                return (
                  <div class={'ftm-menu-element'}>
                    {!characteristicsDisplayed && f.uniqueId.includes('characteristics') && (
                      <div class="characteristic-wrapper">
                        <qds-title layer="main" level="root" tag="h3" alignment="start" >
                          {this.labelProvider(
                            'filters.characteristics-label',
                            'Characteristics',
                          )}
                        </qds-title>
                        {this.tooltip()}
                        {characteristicsDisplayed = true}
                      </div>
                    )}
                    <uiaas-search-facet-filters-node
                      treeNodeProp={JSON.stringify(f)}
                      isRootProp={`${isRoot}`}
                      level={level}
                      rtl={this.rtl}
                      showInputProp={(this.facetsLogicConfig.showFiltersSearchInput && this.facetsLogicConfig.showFiltersSearchInputLimit && f.children.length >= this.facetsLogicConfig.showFiltersSearchInputLimit) ? 'true' : 'false'}
                      onFilterChange={(e) => {
                          this.filterDidChange.emit({
                            filterNode: e.detail,
                            facetCategoryDisplayName: f.displayName,
                            context: FilterChangeContext.desktop
                          });
                      }}
                      collapseFiltersLimit={`${this.facetsLogicConfig.filtersCollapseLimit}`}
                      labels={{
                        'view-more': this.labelProvider('filters.view-more', 'View more'),
                        'view-less': this.labelProvider('filters.view-less', 'View less'),
                        'input-placeholder': this.labelProvider('filters.input.placeholder', 'Search in filter values'),
                        'header-facet-label': this.labelProvider('additional-conditions.banner.new', 'New'),
                      }}
                      filterIcon={this.getIconForFilter(f.id)}
                      mobileView={this.mobileView}
                      key={index + f['count']}
                    >
                      {(f.children.length > 0 && ('selected' in f) && (f.selected || this.facetsLogicConfig.showChildren === 'allways')) ? (this.renderNode(cloneObjectWithNoReference<FacetChildUiModel[]>(f.children), false, level)) : ''}
                    </uiaas-search-facet-filters-node>
                  </div>
                );
              } else {
                return '';
              }
          })}
        </div>
      );
  }

  private getIconForFilter(facetId: string): string {
      if(this.filterPremiumIconUniqueId) {
        const uniqueIdArray = this.filterPremiumIconUniqueId.split(FILTERS_ID_SEPARATOR);
        if(facetId === uniqueIdArray[0]) {
          return JSON.stringify({'showPremium': true, filterValue: uniqueIdArray[uniqueIdArray.length - 1]});
        }
      }
  }
}
